'use client';

/**
 * TestCoverageGraph - Architecture ↔ Testing coverage spiderweb
 *
 * Renders the latest ARUN_ architecture graph with TestEntity nodes
 * overlaid via TESTS edges. Shows the full hierarchy:
 * System → Package → Capability → Feature, with test case coverage.
 *
 * Uses reagraph (same pattern as ProjectGraphReagraph).
 *
 * @component_role Test Coverage Visualization
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain testing
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  Loader2,
  AlertCircle,
  Network,
  ZoomIn,
  ZoomOut,
  Maximize2,
  MousePointer2,
  RefreshCw,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { api } from '@/lib/api/client';

// ---------------------------------------------------------------------------
// Types (aligned with GraphVisualizeResponse)
// ---------------------------------------------------------------------------

interface CoverageGraphNode {
  id: string;
  label: string;
  type: string;
  extra?: Record<string, unknown> | null;
}

interface CoverageGraphEdge {
  id: string;
  source: string;
  target: string;
  type: string;
}

interface CoverageGraphResponse {
  nodes: CoverageGraphNode[];
  edges: CoverageGraphEdge[];
  metadata: {
    query_time_ms: number;
    node_count: number;
    edge_count: number;
    analysis_timestamp?: string | null;
  };
}

// ---------------------------------------------------------------------------
// Styling
// ---------------------------------------------------------------------------

const NODE_COLORS: Record<string, string> = {
  system: '#4A90D9',      // Blue — root
  package: '#7ED321',     // Green — packages
  capability: '#F5A623',  // Orange — capabilities
  feature: '#BD10E0',     // Purple — features
  story: '#50E3C2',       // Teal — user stories
  test_case: '#FF6B6B',   // Coral — test cases
};

const NODE_SIZES: Record<string, number> = {
  system: 16,
  package: 12,
  capability: 8,
  feature: 6,
  story: 5,
  test_case: 10,
};

const EDGE_COLORS: Record<string, string> = {
  HAS_CHILD: '#666666',
  DEPENDS_ON: '#F5A623',
  TESTS: '#FF6B6B',
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface TestCoverageGraphProps {
  entityId?: string | null;  // ARUN_ run_id — if provided, scope graph to this run
}

export function TestCoverageGraph({ entityId: entityIdProp }: TestCoverageGraphProps = {}) {
  const selectNode = useGraphSelectionStore((state) => state.selectNode);
  const selectedNode = useGraphSelectionStore((state) => state.selectedNode);
  const { resolvedTheme } = useTheme();
  const graphRef = useRef<any>(null);

  // Latch run_id: only update when prop changes or a new analysis_run is selected.
  // Clicking nodes inside the graph (package, test_case) must NOT reset the run scope.
  const [lockedRunId, setLockedRunId] = useState<string | null>(entityIdProp ?? null);

  useEffect(() => {
    if (entityIdProp != null) {
      setLockedRunId(entityIdProp);
    } else if (selectedNode?.type === 'analysis_run') {
      setLockedRunId(selectedNode.id);
    }
  }, [entityIdProp, selectedNode?.type === 'analysis_run' ? selectedNode.id : null]);

  const entityId = lockedRunId;

  // Data state
  const [data, setData] = useState<CoverageGraphResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Reagraph dynamic import
  const [isClient, setIsClient] = useState(false);
  const [GraphCanvas, setGraphCanvas] = useState<any>(null);
  const [themes, setThemes] = useState<{ dark: any; light: any } | null>(null);

  // Interaction state
  const [selectedNodes, setSelectedNodes] = useState<string[]>([]);
  const [activeNodes, setActiveNodes] = useState<string[]>([]);
  const [clusteringEnabled, setClusteringEnabled] = useState(true);

  // Sync external selections (e.g. copilot Test Cases panel) into graph highlights.
  // test_case nodes use semantic_id as graph key but case_id in the selection store.
  useEffect(() => {
    if (!selectedNode || !data) return;
    if (selectedNode.type === 'analysis_run') return; // handled by lockedRunId
    let graphId: string | null = null;
    if (selectedNode.type === 'test_case') {
      const graphNode = data.nodes.find(
        (n) => n.type === 'test_case' && n.extra?.case_id === selectedNode.id,
      );
      if (graphNode) graphId = graphNode.id;
    } else {
      const directMatch = data.nodes.find((n) => n.id === selectedNode.id);
      if (directMatch) graphId = directMatch.id;
    }
    if (graphId) {
      setSelectedNodes([graphId]);
      setActiveNodes([graphId]);
    }
  }, [selectedNode, data]);

  // Type filter — which node types are visible
  const FILTERABLE_TYPES = ['story', 'feature', 'capability'] as const;
  const [hiddenTypes, setHiddenTypes] = useState<Set<string>>(new Set(['story']));

  const toggleType = useCallback((type: string) => {
    setHiddenTypes((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  }, []);

  // Fetch coverage graph, optionally scoped to a run
  const fetchGraph = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const url = entityId
        ? `/testing/graph/coverage?run_id=${encodeURIComponent(entityId)}`
        : '/testing/graph/coverage';
      const result = await api.get<CoverageGraphResponse>(url);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load coverage graph');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchGraph();
  }, [entityId]);

  // Load reagraph client-side
  useEffect(() => {
    setIsClient(true);
    import('reagraph').then((mod) => {
      setGraphCanvas(() => mod.GraphCanvas);
      const customDarkTheme = {
        ...mod.darkTheme,
        canvas: { ...mod.darkTheme.canvas, background: '#121212' },
        node: { ...mod.darkTheme.node, activeFill: '#ffffff', inactiveOpacity: 0.3 },
        edge: { ...mod.darkTheme.edge, activeFill: '#ffffff', inactiveOpacity: 0.2 },
        cluster: {
          ...mod.darkTheme.cluster,
          stroke: '#ffffff22',
          fill: '#ffffff11',
          label: { stroke: '#ffffff', color: '#ffffff' },
        },
      };
      const customLightTheme = {
        ...mod.lightTheme,
        canvas: { ...mod.lightTheme.canvas, background: '#ffffff' },
        node: { ...mod.lightTheme.node, activeFill: '#000000', inactiveOpacity: 0.3 },
        edge: { ...mod.lightTheme.edge, activeFill: '#000000', inactiveOpacity: 0.2 },
        cluster: {
          ...mod.lightTheme.cluster,
          stroke: '#00000022',
          fill: '#00000011',
          label: { stroke: '#000000', color: '#000000' },
        },
      };
      setThemes({ dark: customDarkTheme, light: customLightTheme });
    });
  }, []);

  // Transform to reagraph format, applying type filter
  const { nodes, edges, filteredCounts } = useMemo(() => {
    if (!data?.nodes) return { nodes: [], edges: [], filteredCounts: { visible: 0, hidden: 0 } };

    // Filter nodes by type
    const visibleNodes = data.nodes.filter((n) => !hiddenTypes.has(n.type));
    const visibleIds = new Set(visibleNodes.map((n) => n.id));

    const nodes = visibleNodes.map((node) => ({
      id: node.id,
      label: node.label,
      fill: NODE_COLORS[node.type] || '#8AB4F8',
      size: NODE_SIZES[node.type] || 6,
      cluster: node.type,
      data: { type: node.type, ...node.extra },
    }));

    // Only keep edges where both endpoints are visible
    const edges = data.edges
      .filter((e) => visibleIds.has(e.source) && visibleIds.has(e.target))
      .map((edge) => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: edge.type === 'HAS_CHILD' ? '' : edge.type.replace(/_/g, ' '),
        fill: EDGE_COLORS[edge.type] || '#666666',
        size: edge.type === 'TESTS' ? 2 : 1,
      }));

    return {
      nodes,
      edges,
      filteredCounts: {
        visible: visibleNodes.length,
        hidden: data.nodes.length - visibleNodes.length,
      },
    };
  }, [data, hiddenTypes]);

  // Handlers
  const handleNodeClick = useCallback((node: any) => {
    setSelectedNodes([node.id]);
    // Also activate so reagraph highlights connected edges + neighbor nodes
    setActiveNodes([node.id]);
    const nodeType = node.data?.type || 'unknown';
    // For test_case nodes, use case_id (registry key) not semantic_id (graph key)
    const dispatchId = nodeType === 'test_case'
      ? (node.data?.case_id || node.id)
      : node.id;
    selectNode({
      id: dispatchId,
      label: node.label || node.id,
      type: nodeType,
      properties: node.data,
    });
  }, [selectNode]);

  const handleNodePointerOver = useCallback((node: any) => {
    setActiveNodes([node.id]);
  }, []);

  const handleNodePointerOut = useCallback(() => {
    // Keep active state if a node is selected (persist neighbor highlighting)
    if (selectedNodes.length > 0) return;
    setActiveNodes([]);
  }, []);

  const handleZoomIn = useCallback(() => graphRef.current?.zoomIn?.(), []);
  const handleZoomOut = useCallback(() => graphRef.current?.zoomOut?.(), []);
  const handleFitView = useCallback(() => graphRef.current?.fitNodesInView?.(), []);

  const handleCanvasClick = useCallback(() => {
    setSelectedNodes([]);
    setActiveNodes([]);
  }, []);

  // Loading states
  if (!isClient || isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading coverage graph...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-destructive">
        <AlertCircle className="w-12 h-12 mb-4" />
        <p>Failed to load coverage graph</p>
        <p className="text-sm text-muted-foreground mt-2">{error}</p>
        <button
          onClick={fetchGraph}
          className="mt-4 px-3 py-1.5 rounded bg-muted text-sm hover:bg-muted transition-colors"
        >
          <RefreshCw className="w-3 h-3 inline mr-1" /> Retry
        </button>
      </div>
    );
  }

  if (!nodes.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Network className="w-12 h-12 mb-4 opacity-50" />
        <p>No coverage data</p>
        <p className="text-sm mt-2">Run architecture sync and test sync first</p>
      </div>
    );
  }

  if (!GraphCanvas) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
        <Loader2 className="w-12 h-12 animate-spin mb-4" />
        <p>Loading graph renderer...</p>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-panel-border bg-panel">
        <div className="flex items-center gap-2">
          <Network className="w-4 h-4 text-primary" />
          <span className="text-sm font-medium">Architecture Coverage</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setClusteringEnabled(!clusteringEnabled)}
            className={`p-1.5 rounded text-xs transition-colors ${
              clusteringEnabled
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted'
            }`}
            title="Toggle clustering"
          >
            Cluster
          </button>

          {/* Type filters */}
          <div className="flex items-center gap-1 border-l border-panel-border pl-2 ml-1">
            {FILTERABLE_TYPES.map((type) => {
              const isHidden = hiddenTypes.has(type);
              const color = NODE_COLORS[type];
              return (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`flex items-center gap-1 px-2 py-1 rounded text-[11px] transition-colors ${
                    isHidden
                      ? 'bg-muted/50 text-muted-foreground/50 line-through'
                      : 'bg-muted text-foreground'
                  }`}
                  title={`${isHidden ? 'Show' : 'Hide'} ${type} nodes`}
                >
                  <div
                    className="w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: color, opacity: isHidden ? 0.3 : 1 }}
                  />
                  <span className="capitalize">{type}</span>
                </button>
              );
            })}
          </div>

          <button
            onClick={fetchGraph}
            className="p-1.5 rounded hover:bg-muted transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>

          <div className="flex items-center border-l border-panel-border pl-2 ml-1">
            <button onClick={handleZoomIn} className="p-1.5 rounded hover:bg-muted transition-colors" title="Zoom in">
              <ZoomIn className="w-4 h-4" />
            </button>
            <button onClick={handleZoomOut} className="p-1.5 rounded hover:bg-muted transition-colors" title="Zoom out">
              <ZoomOut className="w-4 h-4" />
            </button>
            <button onClick={handleFitView} className="p-1.5 rounded hover:bg-muted transition-colors" title="Fit to view">
              <Maximize2 className="w-4 h-4" />
            </button>
          </div>

          <div className="flex items-center gap-3 text-xs text-muted-foreground border-l border-panel-border pl-2 ml-1">
            <span>{nodes.length} nodes</span>
            <span>{edges.length} edges</span>
            {filteredCounts.hidden > 0 && (
              <span className="text-muted-foreground/50">({filteredCounts.hidden} hidden)</span>
            )}
          </div>
        </div>
      </div>

      {/* Graph */}
      <div className="flex-1 bg-background relative overflow-hidden" style={{ minHeight: '400px' }} suppressHydrationWarning>
        <div className="absolute inset-0">
          <GraphCanvas
            ref={graphRef}
            nodes={nodes}
            edges={edges}
            layoutType="forceDirected2d"
            clusterAttribute={clusteringEnabled ? 'cluster' : undefined}
            labelType="nodes"
            draggable
            animated
            cameraMode="pan"
            edgeArrowPosition="end"
            edgeLabelPosition="above"
            theme={themes ? (resolvedTheme === 'dark' ? themes.dark : themes.light) : undefined}
            selections={selectedNodes}
            actives={activeNodes}
            onNodeClick={handleNodeClick}
            onNodePointerOver={handleNodePointerOver}
            onNodePointerOut={handleNodePointerOut}
            onCanvasClick={handleCanvasClick}
          />
        </div>
      </div>

      {/* Legend — clickable to toggle filterable types */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-panel-border bg-panel text-xs">
        <div className="flex items-center gap-4">
          {Object.entries(NODE_COLORS).map(([type, color]) => {
            const isFilterable = (FILTERABLE_TYPES as readonly string[]).includes(type);
            const isHidden = hiddenTypes.has(type);
            return (
              <div
                key={type}
                className={`flex items-center gap-1 ${isFilterable ? 'cursor-pointer hover:opacity-80' : ''}`}
                onClick={isFilterable ? () => toggleType(type) : undefined}
                title={isFilterable ? `Click to ${isHidden ? 'show' : 'hide'} ${type}` : undefined}
              >
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: color, opacity: isHidden ? 0.3 : 1 }}
                />
                <span
                  className={`capitalize ${isHidden ? 'text-muted-foreground/40 line-through' : 'text-muted-foreground'}`}
                >
                  {type.replace(/_/g, ' ')}
                </span>
              </div>
            );
          })}
        </div>
        <div className="text-muted-foreground">
          <MousePointer2 className="w-3 h-3 inline mr-1" />
          Click to select
        </div>
      </div>
    </div>
  );
}
